// Workforce.js
// Grid: pharmacies down the left, roles across the top. Each cell shows the
// total staff hours for that role in that pharmacy per 1000 items dispensed per
// month — i.e. (sum of contracted hours for that role) / (monthly item count) * 1000.
//
// Colour coding is anchored on the MEDIAN cell value for each role column:
//   - green  : at/near the median (well-staffed relative to peers)
//   - yellow : below median (understaffed for the dispensing volume)
//   - red    : above median (overstaffed for the dispensing volume)
// The scale runs continuously from yellow through green to red.
//
// NOTE: data is hard-coded placeholder for now. The monthly item count will
// eventually come from Periodic Entries ("Item count") per pharmacy; here it is
// supplied inline.

import React, { useMemo } from "react";

/* ---------------- Placeholder dataset ----------------
   itemCount = monthly dispensed items for the pharmacy (stand-in for the
   Periodic Entries "Item count" figure). hoursByRole = total contracted staff
   hours per month for each role at that pharmacy. */
const ROLES = ["Pharmacist", "Technician", "Dispenser", "Counter Assistant"];

const PLACEHOLDER_PHARMACIES = [
  { name: "Riverside Pharmacy",  itemCount: 8200,  hoursByRole: { Pharmacist: 160, Technician: 0,   Dispenser: 150, "Counter Assistant": 80 } },
  { name: "Highstreet Chemist",  itemCount: 11500, hoursByRole: { Pharmacist: 160, Technician: 140, Dispenser: 0,   "Counter Assistant": 100 } },
  { name: "Parkside Pharmacy",   itemCount: 6400,  hoursByRole: { Pharmacist: 120, Technician: 150, Dispenser: 160, "Counter Assistant": 0 } },
  { name: "Canalside Pharmacy",  itemCount: 9800,  hoursByRole: { Pharmacist: 160, Technician: 0,   Dispenser: 0,   "Counter Assistant": 90 } },
  { name: "Meadow Pharmacy",     itemCount: 5200,  hoursByRole: { Pharmacist: 120, Technician: 80,  Dispenser: 120, "Counter Assistant": 60 } },
  { name: "Station Pharmacy",    itemCount: 14200, hoursByRole: { Pharmacist: 200, Technician: 160, Dispenser: 160, "Counter Assistant": 120 } },
  { name: "Orchard Pharmacy",    itemCount: 7100,  hoursByRole: { Pharmacist: 160, Technician: 120, Dispenser: 90,  "Counter Assistant": 70 } }
];

/* Hours per 1000 items for a given pharmacy/role. Returns null when the
   pharmacy has no hours recorded for that role (rendered as a blank cell). */
function hoursPer1000(pharmacy, role) {
  const hrs = pharmacy.hoursByRole[role] || 0;
  if (!pharmacy.itemCount || hrs === 0) return null;
  return (hrs / pharmacy.itemCount) * 1000;
}

function median(nums) {
  const arr = [...nums].sort((a, b) => a - b);
  if (arr.length === 0) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

/* Map a cell value to a background colour, anchored on the column median.
   below median -> yellow (understaffed), at median -> green,
   above median -> red (overstaffed). The further from the median, the more
   saturated, capped at the column's own min/max so the scale always uses its
   full range. */
function cellColour(value, med, min, max) {
  if (value == null || med == null) return "transparent";
  if (value <= med) {
    // yellow -> green
    const span = med - min;
    const t = span > 0 ? (value - min) / span : 1; // 0 at min (most understaffed), 1 at median
    return mix([253, 224, 71], [134, 239, 172], t); // amber-300 -> green-300
  } else {
    // green -> red
    const span = max - med;
    const t = span > 0 ? (value - med) / span : 0; // 0 at median, 1 at max (most overstaffed)
    return mix([134, 239, 172], [252, 165, 165], t); // green-300 -> red-300
  }
}
function mix(a, b, t) {
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * Math.max(0, Math.min(1, t))));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

export default function Workforce() {
  const pharmacies = PLACEHOLDER_PHARMACIES;

  // Pre-compute, per role column, the value distribution so cells can be
  // coloured relative to the column median/min/max.
  const colStats = useMemo(() => {
    const stats = {};
    for (const role of ROLES) {
      const vals = pharmacies
        .map(p => hoursPer1000(p, role))
        .filter(v => v != null);
      stats[role] = {
        med: median(vals),
        min: vals.length ? Math.min(...vals) : null,
        max: vals.length ? Math.max(...vals) : null
      };
    }
    return stats;
  }, [pharmacies]);

  const th = {
    textAlign: "center", padding: "10px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: "#374151",
    borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap"
  };
  const td = {
    padding: "10px 12px", fontSize: 13, textAlign: "center",
    borderBottom: "1px solid #f0f1f3", borderLeft: "1px solid #f0f1f3"
  };

  return (
    <div style={{ padding: "16px 20px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Workforce</h2>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        Staff hours per 1,000 items dispensed per month, by role. Colour is relative to the median pharmacy
        for each role — green is on the median, yellow understaffed, red overstaffed.
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Pharmacy</th>
              {ROLES.map(role => (
                <th key={role} style={th}>
                  {role}
                  <div style={{ fontSize: 9, fontWeight: 400, color: "#6b7280", textTransform: "none", letterSpacing: 0 }}>
                    Hours per 1000 items
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pharmacies.map(p => (
              <tr key={p.name}>
                <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>
                  {p.name}
                  <div style={{ fontSize: 10, fontWeight: 400, color: "#6b7280" }}>
                    {p.itemCount.toLocaleString()} items/mth
                  </div>
                </td>
                {ROLES.map(role => {
                  const v = hoursPer1000(p, role);
                  const s = colStats[role];
                  const bg = cellColour(v, s.med, s.min, s.max);
                  return (
                    <td key={role} style={{ ...td, background: bg }}>
                      {v == null ? <span style={{ color: "#9ca3af" }}>—</span> : v.toFixed(1)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 18, marginTop: 12, fontSize: 12, color: "#374151", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 28, height: 12, background: "linear-gradient(90deg, rgb(253,224,71), rgb(134,239,172), rgb(252,165,165))", borderRadius: 2 }} />
          Understaffed → median → overstaffed
        </span>
        <span style={{ color: "#6b7280" }}>(— = no hours recorded for that role)</span>
      </div>
    </div>
  );
}
